import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/supabase/client";
import { upsertCharacters } from "@/lib/storybooksApi";

interface CharactersModalProps {
  open: boolean;
  onClose?: () => void;
  classroomId: string | null | undefined;
  onSaved?: (characters: Character[]) => void;
}

interface Participant {
  id: string;
  display_name: string | null;
  photo_urls: string[] | null;
}

interface CharacterDescriptor {
  hair?: string;
  skin?: string;
  accessory?: string;
  [key: string]: string | undefined;
}

interface CharacterForm {
  name: string;
  ref_images: string[];
  descriptor: CharacterDescriptor;
}

interface Character {
  participant_id: string;
  name: string;
  ref_images: string[];
  descriptor: CharacterDescriptor;
}

interface DraftState {
  [participantId: string]: CharacterForm;
}

export default function CharactersModal({ open, onClose, classroomId, onSaved }: CharactersModalProps) {
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [draft, setDraft] = useState<DraftState>({}); // { participant_id: { name, ref_images[], descriptor:{} } }
  const disabled = !classroomId || loading;

  useEffect(() => {
    if (!open || !classroomId) return;
    let active = true;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("participants")
        .select("id, display_name, photo_urls")
        .eq("classroom_id", classroomId)
        .order("display_name", { ascending: true });
      if (!active) return;
      if (!error) setParticipants((data as Participant[]) || []);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [open, classroomId]);

  const handleToggleRef = (pid: string, url: string) => {
    setDraft(prev => {
      const cur = prev[pid] || { name: "", ref_images: [], descriptor: {} };
      const has = cur.ref_images.includes(url);
      const nextRefs = has ? cur.ref_images.filter(u => u !== url) : [...cur.ref_images, url].slice(0,3);
      return { ...prev, [pid]: { ...cur, ref_images: nextRefs } };
    });
  };

  const handleName = (pid: string, val: string) => {
    setDraft(prev => ({ ...prev, [pid]: { ...(prev[pid] || { ref_images: [], descriptor: {} }), name: val } }));
  };

  const handleTrait = (pid: string, key: string, val: string) => {
    setDraft(prev => {
      const cur = prev[pid] || { name: "", ref_images: [], descriptor: {} };
      return { ...prev, [pid]: { ...cur, descriptor: { ...(cur.descriptor || {}), [key]: val } } };
    });
  };

  const rows = useMemo(() => participants.map((p) => {
    const d: CharacterForm = draft[p.id] || { name: p.display_name || "", ref_images: [], descriptor: {} };
    return { participant: p, form: d };
  }), [participants, draft]);

  const save = async () => {
    try {
      setLoading(true);
      const payload = rows
        .filter(r => (r.form.name || "").trim() && r.form.ref_images?.length)
        .map(r => ({
          participant_id: r.participant.id,
          name: r.form.name.trim(),
          ref_images: r.form.ref_images.slice(0,3),
          descriptor: r.form.descriptor || {},
        }));
      if (!payload.length) { onClose?.(); return; }

      const json = await upsertCharacters({
        classroom_id: classroomId!,
        characters: payload,
      });
      onSaved?.(Array.isArray(json?.data?.characters) ? json.data.characters : []);
      onClose?.();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl rounded-3xl bg-white shadow-xl p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-cocoa">Build characters from photos</h3>
          <button className="text-cocoa/60 hover:text-cocoa" onClick={onClose}>✕</button>
        </div>

        <p className="mt-2 text-sm text-cocoa/70">
          Pick up to 3 reference photos per student, give them a character name, and (optionally) add quick traits.
        </p>

        <div className="mt-4 max-h-[60vh] overflow-auto space-y-4">
          {loading && <p className="text-sm text-cocoa/70">Loading…</p>}
          {!loading && rows.length === 0 && (
            <p className="text-sm text-cocoa/70">No participants found yet. Upload photos first.</p>
          )}

          {rows.map(({ participant, form }) => (
            <div key={participant.id} className="rounded-2xl border border-sand p-4 bg-shell/40">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-[220px]">
                  <label className="text-xs text-cocoa/60">Character name</label>
                  <input
                    className="input mt-1"
                    value={form.name}
                    onChange={e => handleName(participant.id, e.target.value)}
                    placeholder={participant.display_name || "Name"}
                  />
                </div>
                <div className="flex gap-3">
                  <div>
                    <label className="text-xs text-cocoa/60">Hair</label>
                    <input className="input mt-1 w-36" value={form.descriptor?.hair || ""}
                      onChange={e => handleTrait(participant.id, "hair", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-cocoa/60">Skin tone</label>
                    <input className="input mt-1 w-36" value={form.descriptor?.skin || ""}
                      onChange={e => handleTrait(participant.id, "skin", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-cocoa/60">Accessory</label>
                    <input className="input mt-1 w-36" value={form.descriptor?.accessory || ""}
                      onChange={e => handleTrait(participant.id, "accessory", e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {(participant.photo_urls || []).map((url) => {
                  const chosen = form.ref_images?.includes(url);
                  return (
                    <button
                      key={url}
                      type="button"
                      onClick={() => handleToggleRef(participant.id, url)}
                      className={`relative aspect-square rounded-xl overflow-hidden border ${chosen ? "border-brick ring-2 ring-brick/40" : "border-sand"}`}
                      title={chosen ? "Selected" : "Select"}
                    >
                      {/* eslint-disable-next-line jsx-a11y/alt-text */}
                      <img src={url} className="h-full w-full object-cover" />
                      {chosen && (
                        <span className="absolute top-1 right-1 inline-flex items-center justify-center text-[10px] px-2 py-0.5 rounded-full bg-brick text-white">ref</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button className="btn-secondary" onClick={onClose} disabled={disabled}>Cancel</button>
          <button className="btn" onClick={save} disabled={disabled}>{loading ? "Saving…" : "Save characters"}</button>
        </div>
      </div>
    </div>
  );
}
